import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, isRef as _isRef, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "grid grid-cols-1 lg:grid-cols-2 gap-6" };
import { computed, ref, watch } from "vue";
import { RouterLink } from "vue-router";
import { DatePickerInput, NumberInput, SelectInput, TextInput, WidgetShell, } from "@eudonet/opal";
import { useI18n } from "vue-i18n";
import { buildDonorRoute } from "../../composables/donorRoute";
import { useDateIsoBinding } from "@/shared/composables";
import { useDisplayLocale } from "@/shared/composables/useDisplayLocale";
import { toDatePickerFormat } from "@/shared/utils/date-formatting";
import { currencyLabel, currencySymbol } from "@/shared/utils/workspaceLocaleLabels";
import { useProfileStore } from "@/modules/settings/stores/useProfileStore";
import { useDonationOptions } from "../../composables/useDonationOptions";
export default /*@__PURE__*/ _defineComponent({
    __name: 'SubscriptionTabDetails',
    props: {
        state: {},
        readonly: { type: Boolean }
    },
    emits: ["update:field"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        /**
         * A single field edit. `key` is constrained to a real {@link Subscription} field, so a typo or
         * a renamed field fails to compile instead of silently writing junk into the parent's state,
         * and the value is bounded to the union of subscription field types rather than `unknown`.
         *
         * `null` is the explicit "user cleared this input" signal for the optional number and date
         * fields — the domain models absent as `undefined`, so the parent normalizes it.
         *
         * Per-key key↔value correlation (a mapped union of tuples) does not survive Vue's emit
         * codegen — it collapses the handler prop to `() => any` on the parent side — so the value is
         * typed as the field-value union instead.
         */
        const emit = __emit;
        const { t, locale } = useI18n();
        const profileStore = useProfileStore();
        const { paymentMethodOptionsFor, isSystemPaymentMethod } = useDonationOptions();
        const recordPaymentMethodOptions = paymentMethodOptionsFor(() => [props.state.paymentMethod]);
        const dateFormat = computed(() => toDatePickerFormat(profileStore.profile?.dateFormat, locale.value));
        const donorRoute = computed(() => buildDonorRoute(props.state));
        const displayLocale = useDisplayLocale();
        const currencyPrefix = computed(() => props.state.currency ? currencySymbol(props.state.currency, displayLocale.value) : "");
        const currencyDisplay = computed(() => props.state.currency ? currencyLabel(props.state.currency, locale.value) : "");
        // ─── Date bindings: each editable date keeps an ISO holder synced to the locale-formatted picker ──
        const profileDateFormat = computed(() => profileStore.profile?.dateFormat);
        const startDateRef = ref(props.state.startDate ?? "");
        watch(() => props.state.startDate, (v) => { startDateRef.value = v ?? ""; });
        const startDateDisplay = useDateIsoBinding(startDateRef, profileDateFormat, locale);
        watch(startDateRef, (v, old) => { if (v !== old)
            emit("update:field", "startDate", v || null); });
        const endDateRef = ref(props.state.endDate ?? "");
        watch(() => props.state.endDate, (v) => { endDateRef.value = v ?? ""; });
        const endDateDisplay = useDateIsoBinding(endDateRef, profileDateFormat, locale);
        watch(endDateRef, (v, old) => { if (v !== old)
            emit("update:field", "endDate", v || null); });
        const nextDueDateRef = ref(props.state.nextDueDate ?? "");
        watch(() => props.state.nextDueDate, (v) => { nextDueDateRef.value = v ?? ""; });
        const nextDueDateDisplay = useDateIsoBinding(nextDueDateRef, profileDateFormat, locale);
        watch(nextDueDateRef, (v, old) => { if (v !== old)
            emit("update:field", "nextDueDate", v || null); });
        // ─── Options ─────────────────────────────────────────────────────────────────
        const statusOptions = computed(() => [
            { value: "Active", label: t("finance.subscriptions.status.Active") },
            { value: "Paused", label: t("finance.subscriptions.status.Paused") },
            { value: "Canceled", label: t("finance.subscriptions.status.Canceled") },
            { value: "Expired", label: t("finance.subscriptions.status.Expired") },
            // Failed is the payment provider's to set: listed only so a failed subscription shows its status.
            ...(props.state.status === "Failed"
                ? [{ value: "Failed", label: t("finance.subscriptions.status.Failed") }]
                : []),
        ]);
        const frequencyOptions = computed(() => [
            { value: "Monthly", label: t("finance.subscriptions.frequency.Monthly") },
            { value: "Quarterly", label: t("finance.subscriptions.frequency.Quarterly") },
            { value: "Biannual", label: t("finance.subscriptions.frequency.Biannual") },
            { value: "Annual", label: t("finance.subscriptions.frequency.Annual") },
        ]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(WidgetShell), {
                    header: _ctx.$t('finance.subscriptions.detail.generalInfo')
                }, {
                    default: _withCtx(() => [
                        _createVNode(_unref(TextInput), {
                            label: _ctx.$t('finance.subscriptions.fields.id'),
                            "model-value": __props.state.id,
                            disabled: true,
                            "full-width": ""
                        }, null, 8 /* PROPS */, ["label", "model-value"]),
                        _createVNode(_unref(TextInput), {
                            label: _ctx.$t('finance.subscriptions.fields.merchantId'),
                            "model-value": __props.state.merchantId ?? '',
                            disabled: true,
                            "full-width": ""
                        }, null, 8 /* PROPS */, ["label", "model-value"]),
                        (donorRoute.value)
                            ? (_openBlock(), _createBlock(_unref(RouterLink), {
                                key: 0,
                                to: donorRoute.value,
                                class: "block",
                                "aria-label": `${_ctx.$t('finance.subscriptions.fields.donor')}: ${__props.state.donorName}`
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_unref(TextInput), {
                                        label: _ctx.$t('finance.subscriptions.fields.donor'),
                                        "model-value": __props.state.donorName,
                                        disabled: true,
                                        "value-variant": "link",
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["label", "model-value"])
                                ]),
                                _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["to", "aria-label"]))
                            : (_openBlock(), _createBlock(_unref(TextInput), {
                                key: 1,
                                label: _ctx.$t('finance.subscriptions.fields.donor'),
                                "model-value": __props.state.donorName,
                                disabled: true,
                                "full-width": ""
                            }, null, 8 /* PROPS */, ["label", "model-value"])),
                        _createVNode(_unref(SelectInput), {
                            "model-value": __props.state.status,
                            label: _ctx.$t('finance.subscriptions.columns.status'),
                            options: statusOptions.value,
                            readonly: __props.readonly,
                            "full-width": "",
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = (v) => _ctx.$emit('update:field', 'status', v))
                        }, null, 8 /* PROPS */, ["model-value", "label", "options", "readonly"]),
                        (__props.state.campaignId)
                            ? (_openBlock(), _createBlock(_unref(RouterLink), {
                                key: 2,
                                to: { name: 'CampaignDetail', params: { id: __props.state.campaignId } },
                                class: "block",
                                "aria-label": `${_ctx.$t('finance.subscriptions.fields.campaignLinked')}: ${__props.state.campaignName ?? ''}`
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_unref(TextInput), {
                                        label: _ctx.$t('finance.subscriptions.fields.campaignLinked'),
                                        "model-value": __props.state.campaignName ?? '',
                                        disabled: true,
                                        "value-variant": "link",
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["label", "model-value"])
                                ]),
                                _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["to", "aria-label"]))
                            : (_openBlock(), _createBlock(_unref(TextInput), {
                                key: 3,
                                label: _ctx.$t('finance.subscriptions.fields.campaignLinked'),
                                "model-value": '',
                                disabled: true,
                                "full-width": ""
                            }, null, 8 /* PROPS */, ["label"])),
                        _createVNode(_unref(DatePickerInput), {
                            modelValue: _unref(startDateDisplay),
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (_isRef(startDateDisplay) ? (startDateDisplay).value = $event : null)),
                            label: _ctx.$t('finance.subscriptions.fields.startDate'),
                            format: dateFormat.value,
                            language: _unref(locale),
                            "week-start": 1,
                            readonly: __props.readonly,
                            "full-width": ""
                        }, null, 8 /* PROPS */, ["modelValue", "label", "format", "language", "readonly"]),
                        _createVNode(_unref(DatePickerInput), {
                            modelValue: _unref(endDateDisplay),
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => (_isRef(endDateDisplay) ? (endDateDisplay).value = $event : null)),
                            label: _ctx.$t('finance.subscriptions.fields.endDate'),
                            format: dateFormat.value,
                            language: _unref(locale),
                            "week-start": 1,
                            readonly: __props.readonly,
                            "full-width": ""
                        }, null, 8 /* PROPS */, ["modelValue", "label", "format", "language", "readonly"]),
                        _createVNode(_unref(DatePickerInput), {
                            modelValue: _unref(nextDueDateDisplay),
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => (_isRef(nextDueDateDisplay) ? (nextDueDateDisplay).value = $event : null)),
                            label: _ctx.$t('finance.subscriptions.fields.nextDueDate'),
                            format: dateFormat.value,
                            language: _unref(locale),
                            "week-start": 1,
                            readonly: __props.readonly,
                            "full-width": ""
                        }, null, 8 /* PROPS */, ["modelValue", "label", "format", "language", "readonly"]),
                        _createVNode(_unref(NumberInput), {
                            "model-value": __props.state.installmentCount ?? undefined,
                            label: _ctx.$t('finance.subscriptions.fields.installmentCount'),
                            min: 0,
                            step: 1,
                            readonly: __props.readonly,
                            "full-width": "",
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = (v) => _ctx.$emit('update:field', 'installmentCount', v === '' || v == null ? null : Number(v)))
                        }, null, 8 /* PROPS */, ["model-value", "label", "readonly"])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["header"]),
                _createVNode(_unref(WidgetShell), {
                    header: _ctx.$t('finance.subscriptions.detail.amountAndCurrency')
                }, {
                    default: _withCtx(() => [
                        _createVNode(_unref(NumberInput), {
                            "model-value": __props.state.initialAmount,
                            label: _ctx.$t('finance.subscriptions.fields.initialAmount'),
                            prefix: currencyPrefix.value,
                            min: 0,
                            step: 0.01,
                            readonly: __props.readonly,
                            "full-width": "",
                            "onUpdate:modelValue": _cache[5] || (_cache[5] = (v) => _ctx.$emit('update:field', 'initialAmount', v === '' || v == null ? null : Number(v)))
                        }, null, 8 /* PROPS */, ["model-value", "label", "prefix", "readonly"]),
                        _createVNode(_unref(NumberInput), {
                            "model-value": __props.state.amount,
                            label: _ctx.$t('finance.subscriptions.fields.amount'),
                            prefix: currencyPrefix.value,
                            min: 0,
                            step: 0.01,
                            readonly: __props.readonly,
                            "full-width": "",
                            "onUpdate:modelValue": _cache[6] || (_cache[6] = (v) => _ctx.$emit('update:field', 'amount', Number(v)))
                        }, null, 8 /* PROPS */, ["model-value", "label", "prefix", "readonly"]),
                        _createVNode(_unref(TextInput), {
                            "model-value": currencyDisplay.value,
                            label: _ctx.$t('finance.subscriptions.fields.currency'),
                            readonly: "",
                            "full-width": "",
                            "data-testid": "subscription-detail-currency"
                        }, null, 8 /* PROPS */, ["model-value", "label"]),
                        _createVNode(_unref(SelectInput), {
                            "model-value": __props.state.frequency,
                            label: _ctx.$t('finance.subscriptions.fields.frequency'),
                            options: frequencyOptions.value,
                            readonly: __props.readonly,
                            "full-width": "",
                            "onUpdate:modelValue": _cache[7] || (_cache[7] = (v) => _ctx.$emit('update:field', 'frequency', v))
                        }, null, 8 /* PROPS */, ["model-value", "label", "options", "readonly"]),
                        _createVNode(_unref(SelectInput), {
                            "model-value": __props.state.paymentMethod,
                            label: _ctx.$t('finance.subscriptions.fields.paymentMethod'),
                            options: _unref(recordPaymentMethodOptions),
                            disabled: _unref(isSystemPaymentMethod)(__props.state.paymentMethod),
                            readonly: __props.readonly,
                            "full-width": "",
                            "onUpdate:modelValue": _cache[8] || (_cache[8] = (v) => _ctx.$emit('update:field', 'paymentMethod', v))
                        }, null, 8 /* PROPS */, ["model-value", "label", "options", "disabled", "readonly"])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["header"])
            ]));
        };
    }
});
